import { useEffect, useState } from "react";
import { FiUpload, FiX } from "react-icons/fi";
import { apiRequest } from "../../services/api";

const toSlug = (value) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const getImageUrl = (storageKey) => {
  if (!storageKey) return "";

  const baseUrl = (
    process.env.REACT_APP_ASSET_URL ||
    process.env.REACT_APP_API_URL ||
    ""
  ).replace(/\/$/, "");

  return storageKey.startsWith("http")
    ? storageKey
    : `${baseUrl}${storageKey.startsWith("/") ? "" : "/"}${storageKey}`;
};

export default function SubCategoryModal({
  open,
  onClose,
  onSave,
  subCategory = null,
  categories = [],
  sizeGroups = [],
  accessToken,
}) {
  const [form, setForm] = useState({
    categoryId: "",
    sizeGroupId: "",
    name: "",
    code: "",
    description: "",
    status: "Active",
    imageKey: "",
  });
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  useEffect(() => {
    if (!open) return;

    setError("");
    setForm({
      categoryId: subCategory?.categoryId || "",
      sizeGroupId: subCategory?.sizeGroupId || "",
      name: subCategory?.name || "",
      code: subCategory?.code || "",
      description: subCategory?.description || "",
      status: subCategory?.isActive === false ? "Inactive" : "Active",
      imageKey: subCategory?.imageKey || "",
    });
  }, [open, subCategory]);

  if (!open) return null;

  const handleImageChange = async (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    try {
      setIsUploadingImage(true);
      setError("");

      const formData = new FormData();
      formData.append("file", file);

      const uploaded = await apiRequest("/uploads/subcategories", {
        method: "POST",
        body: formData,
        accessToken,
      });

      setForm((previous) => ({
        ...previous,
        imageKey: uploaded.storageKey,
      }));
    } catch (requestError) {
      setError(requestError.message || "Unable to upload subcategory image.");
    } finally {
      setIsUploadingImage(false);
      event.target.value = "";
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.categoryId || !form.name.trim() || !form.code.trim()) {
      setError("Parent category, name, and code are required.");
      return;
    }

    try {
      setIsSaving(true);
      setError("");

      await onSave({
        categoryId: form.categoryId,
        sizeGroupId: form.sizeGroupId || undefined,
        name: form.name.trim(),
        code: form.code.trim().toUpperCase(),
        slug: toSlug(form.name),
        description: form.description.trim() || undefined,
        isActive: form.status === "Active",
        imageKey: form.imageKey || undefined,
      });

      onClose();
    } catch (requestError) {
      const message = requestError?.details?.message || requestError.message;

      setError(
        Array.isArray(message)
          ? message.join(", ")
          : message || "Unable to save subcategory.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-8 py-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {subCategory ? "Edit Subcategory" : "Add Subcategory"}
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              Create and manage product subcategories.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
          >
            <FiX size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6 p-8">
            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                {error}
              </div>
            )}

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Parent Category *
              </label>

              <select
                value={form.categoryId}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    categoryId: event.target.value,
                  }))
                }
                className="h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700"
                required
              >
                <option value="">Select Category</option>

                {categories
                  .filter((category) => category.isActive)
                  .map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name} ({category.code})
                    </option>
                  ))}
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Size Group
              </label>

              <select
                value={form.sizeGroupId}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    sizeGroupId: event.target.value,
                  }))
                }
                className="h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700"
              >
                <option value="">No size group required</option>

                {sizeGroups
                  .filter((sizeGroup) => sizeGroup.isActive)
                  .map((sizeGroup) => (
                    <option key={sizeGroup.id} value={sizeGroup.id}>
                      {sizeGroup.name} ({sizeGroup.code})
                    </option>
                  ))}
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Subcategory Name *
              </label>

              <input
                type="text"
                value={form.name}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    name: event.target.value,
                  }))
                }
                placeholder="Bracelets"
                className="h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700"
                required
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Subcategory Code *
              </label>

              <input
                type="text"
                value={form.code}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    code: event.target.value.toUpperCase(),
                  }))
                }
                placeholder="BRACELETS"
                className="h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700"
                required
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Description
              </label>

              <textarea
                rows="4"
                value={form.description}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    description: event.target.value,
                  }))
                }
                placeholder="Enter description..."
                className="w-full resize-none rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-primary-700"
              />
            </div>

            <div>
              <label className="mb-3 block text-sm font-medium text-gray-700">
                Status
              </label>

              <div className="flex gap-3">
                {["Active", "Inactive"].map((status) => (
                  <button
                    key={status}
                    type="button"
                    onClick={() =>
                      setForm((previous) => ({
                        ...previous,
                        status,
                      }))
                    }
                    className={`rounded-xl border px-5 py-2.5 font-medium ${
                      form.status === status
                        ? "border-primary-700 bg-primary-700 text-white"
                        : "border-gray-200 bg-white text-gray-700"
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Subcategory Image
              </label>

              <label className="flex h-32 cursor-pointer flex-col items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-gray-300 hover:border-primary-700">
                {form.imageKey ? (
                  <img
                    src={getImageUrl(form.imageKey)}
                    alt="Subcategory preview"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <>
                    <FiUpload size={24} className="mb-2 text-gray-400" />
                    <span className="text-sm text-gray-500">
                      {isUploadingImage
                        ? "Uploading image…"
                        : "Upload optional subcategory image"}
                    </span>
                  </>
                )}

                <input
                  type="file"
                  disabled={isUploadingImage}
                  hidden
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t border-gray-100 px-8 py-5">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="h-11 rounded-xl border border-gray-200 px-6 font-medium disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={isSaving}
              className="h-11 rounded-xl bg-primary-700 px-6 font-medium text-white hover:bg-primary-800 disabled:opacity-60"
            >
              {isSaving ? "Saving..." : "Save Subcategory"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
